import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const languages = [
    { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'zh', name: '简体中文', flag: '🇨🇳' },
    { code: 'zh-Hant', name: '繁體中文', flag: '🇹🇼' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' }
  ];

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('pdf-tools-lang', lng);
    setIsLangOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentLanguage = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Site Name */}
          <Link to="/" className="flex items-center gap-3 group cursor-pointer">
            <img 
              src="https://public.readdy.ai/ai/img_res/bb3a3b5e-bd00-4be3-a469-cabdd447c0ab.png" 
              alt="PDF Tools Logo" 
              className="w-10 h-10 object-contain transition-transform group-hover:scale-110"
            />
            <span className="text-xl font-bold text-gray-900 font-['Space_Grotesk'] hidden sm:block">
              {t('common:siteName')}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link 
              to="/" 
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('common:nav.home')}
            </Link>
            <Link 
              to="/pdf-to-word" 
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('common:nav.pdfToWord')}
            </Link>
            <Link 
              to="/pdf-to-excel" 
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('common:nav.pdfToExcel')}
            </Link>
            <Link 
              to="/pdf-to-ppt" 
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('common:nav.pdfToPpt')}
            </Link>
            <Link 
              to="/image-convert" 
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('common:nav.imageConvert')}
            </Link>
            <Link 
              to="/pdf-tools" 
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('common:nav.pdfTools')}
            </Link>
            <Link 
              to="/tutorials" 
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('common:nav.tutorials')}
            </Link>
            <Link 
              to="/faq" 
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('common:nav.faq')}
            </Link>
          </div>

          {/* Language Switcher and Mobile Menu Button */}
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:border-teal-500 hover:text-teal-600 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-global-line text-lg"></i>
                <span className="text-base">{currentLanguage.flag}</span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform ${isLangOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isLangOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setIsLangOpen(false)}
                  ></div>
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border-2 border-gray-100 py-2 z-50">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full px-4 py-3 text-left text-sm hover:bg-teal-50 transition-colors flex items-center gap-3 cursor-pointer ${
                          i18n.language === lang.code ? 'bg-teal-50 text-teal-600 font-semibold' : 'text-gray-700'
                        }`}
                      >
                        <span className="text-xl">{lang.flag}</span>
                        <span>{lang.name}</span>
                        {i18n.language === lang.code && (
                          <i className="ri-check-line text-lg ml-auto"></i>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden flex items-center justify-center w-10 h-10 text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 py-4 bg-white">
            <div className="flex flex-col gap-2">
              <Link 
                to="/" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer"
              >
                {t('common:nav.home')}
              </Link>
              <Link 
                to="/pdf-to-word" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer"
              >
                {t('common:nav.pdfToWord')}
              </Link>
              <Link 
                to="/pdf-to-excel" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer"
              >
                {t('common:nav.pdfToExcel')}
              </Link>
              <Link 
                to="/pdf-to-ppt" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer"
              >
                {t('common:nav.pdfToPpt')}
              </Link>
              <Link 
                to="/image-convert" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer"
              >
                {t('common:nav.imageConvert')}
              </Link>
              <Link 
                to="/pdf-tools" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer"
              >
                {t('common:nav.pdfTools')}
              </Link>
              <Link 
                to="/tutorials" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer"
              >
                {t('common:nav.tutorials')}
              </Link>
              <Link 
                to="/faq" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer"
              >
                {t('common:nav.faq')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;